import React, { useEffect, useState } from 'react';
import { getInviteByTokenCore, verifyInviteCodeCore } from '@gv/core';
import { supabase } from '../lib/supabase';
import { useGroupContext } from '../lib/groups/context';

type Props = {
    token: string;
    onDone?: () => void; // called after accept/decline is handled
};

export default function InviteAcceptPage({ token, onDone }: Props) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [vaultName, setVaultName] = useState<string>('');
    const [vaultSlug, setVaultSlug] = useState<string>('');
    const [vaultLogo, setVaultLogo] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [verifying, setVerifying] = useState<boolean>(false);
    const { reload } = useGroupContext();

    useEffect(() => {
        (async () => {
            try {
                setError(null);
                setLoading(true);
                const data = await getInviteByTokenCore(supabase as any, token);
                if (!data) {
                    setError('This invite link is invalid or has expired.');
                    return;
                }
                const { invite, vault } = data;
                setEmail(invite.email);
                if (vault) {
                    setVaultName(vault.name || 'Vault');
                    setVaultSlug(vault.slug || '');
                    setVaultLogo((vault as any).image_url || '');
                }
            } catch (e: any) {
                setError(e?.message || 'Failed to load invite');
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const clearInviteFromUrl = () => {
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            window.history.replaceState({}, document.title, url.toString());
        } catch { /* ignore */ }
    };

    const handleAccept = async () => {
        try {
            setVerifying(true);
            setError(null);
            const clean = (code || '').replace(/[^0-9]/g, '').slice(0, 6);
            if (clean.length !== 6) {
                setError('Enter the 6-digit invite code from the inviter.');
                setVerifying(false);
                return;
            }
            const ok = await verifyInviteCodeCore(supabase as any, token, clean);
            if (!ok) {
                setError('Wrong code—double-check and try again.');
                setVerifying(false);
                return;
            }
            clearInviteFromUrl();
            await reload();
            if (vaultSlug) {
                try {
                    window.history.pushState({}, '', `/${vaultSlug}`);
                    await reload();
                } catch {
                    window.location.href = `/${vaultSlug}`;
                }
            } else {
                try {
                    window.history.pushState({}, '', '/');
                    await reload();
                } catch {
                    window.location.href = '/';
                }
            }
            if (onDone) onDone();
        } catch (e: any) {
            const msg = (e?.message || '').toString();
            if (/invalid invite code|invalid|code|wrong/i.test(msg)) setError('Wrong code—double-check and try again.');
            else setError('Failed to accept invite');
        } finally {
            setVerifying(false);
        }
    };

    const handleDecline = () => {
        // Just clear token and navigate home per spec
        clearInviteFromUrl();
        try {
            window.history.pushState({}, '', '/');
            reload();
        } catch {
            window.location.href = '/';
        }
        if (onDone) onDone();
    };

    return (
        <div className="min-h-screen bg-black flex items-center justify-center px-6">
            <div className="max-w-lg w-full text-center text-white">
                {vaultLogo ? (
                    <img src={vaultLogo} alt="Vault Logo" className="h-14 w-14 mx-auto mb-6 object-contain" />
                ) : (
                    <img src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp" alt="Vault Logo" className="h-14 w-14 mx-auto mb-6 object-contain" />
                )}
                {loading ? (
                    <>
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
                        <p className="text-gray-400">Loading invitation…</p>
                    </>
                ) : error ? (
                    <>
                        <h1 className="text-2xl font-light tracking-wide mb-2">Invitation Error</h1>
                        <p className="text-gray-400 mb-6">{error}</p>
                        <button
                            onClick={handleDecline}
                            className="px-5 py-3 font-semibold bg-white text-black hover:bg-gray-100"
                        >
                            GO HOME
                        </button>
                    </>
                ) : (
                    <>
                        <h1 className="text-2xl font-light tracking-wide mb-2">You’re invited</h1>
                        <p className="text-gray-400 mb-6">
                            {email ? (
                                <>The account <span className="text-white">{email}</span> has been invited to join {vaultName ? (<span className="text-white">{vaultName}</span>) : (<>this vault</>)}.</>
                            ) : (
                                <>You’ve been invited to join {vaultName ? (<span className="text-white">{vaultName}</span>) : (<>this vault</>)}.</>
                            )}
                        </p>
                        <div className="mb-4 text-left">
                            <label className="block text-sm text-gray-400 mb-1">Invite code</label>
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                onPaste={(e) => {
                                    try {
                                        const txt = e.clipboardData?.getData('text') || '';
                                        const m = txt.match(/\b(\d{6})\b/);
                                        if (m) {
                                            e.preventDefault();
                                            setCode(m[1]);
                                        }
                                    } catch { }
                                }}
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="6-digit code from inviter"
                                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={handleAccept}
                                className="px-5 py-3 font-semibold bg-white text-black hover:bg-gray-100 disabled:opacity-60"
                                disabled={verifying}
                            >
                                {verifying ? 'VERIFYING…' : 'ACCEPT INVITE'}
                            </button>
                            <button
                                onClick={handleDecline}
                                className="px-5 py-3 font-semibold bg-gray-900 text-gray-300 hover:bg-gray-800 border border-gray-700"
                                disabled={verifying}
                            >
                                DECLINE
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
